import { Auth } from "./../../services/auth";
import router from "./../../router";
import { isPatient, isDoctor, isReceptionist } from "../../utils/userRole";
import {
  isPreLaunchBlocked,
  preLaunchBlockedMessage
} from "../../utils/apiErrors";

function doctorHasBasicInfo(currentUser) {
  return !!(currentUser.address?.id && currentUser.bank_account);
}

async function fetchHasSlots(dispatch, currentUser) {
  try {
    const res = await dispatch(
      "doctor/fetchDoctorsAvailables",
      { id: currentUser.id },
      { root: true }
    );
    const slots = res?.data ?? [];
    return slots.length > 0;
  } catch {
    return false;
  }
}

async function fetchDocsStatus(dispatch, rootGetters) {
  try {
    await dispatch("doctorDocument/fetchDocuments", {}, { root: true });
  } catch {}
  return {
    done:
      rootGetters["doctorDocument/allDocumentsSent"] &&
      !rootGetters["doctorDocument/hasDocumentsInAnalysis"] &&
      !rootGetters["doctorDocument/hasDocumentsRejected"],
    inAnalysis: rootGetters["doctorDocument/hasDocumentsInAnalysis"]
  };
}

export const state = {
  alertError: false,
  loggedIn: false,
  redirectFrom: null,
  userGuide: false,
  verifiedAccount: false,
  hasActiveRelationshipWithInstitutions: false,
  isSubmitting: false,
  loggedUser: {}
};

export const getters = {
  loggedIn(state) {
    return state.loggedIn;
  },

  loggedUser(state) {
    return state.loggedUser;
  },

  redirectPathName(state, getters, rootState) {
    // US-C12: senha provisória é trocada antes de qualquer destino. Tipo de
    // conta, unidades e a flag vêm do usuário do login (o user.currentUser é
    // o do users#show, que não traz esses campos).
    const loginUser = state.loggedUser || {};
    if (loginUser.must_change_password) return "/trocar-senha";

    const schedulingAppointment =
      rootState.appointments.currentSchedule &&
      rootState.appointments.currentSchedule.slot_id;

    if (schedulingAppointment) return "/agendamento";
    if (state.redirectFrom) return state.redirectFrom;

    const currentUser = rootState.user.currentUser;

    // Paciente sempre vai para a home
    if (isPatient(currentUser)) return "/";

    // Médico: verificar se completou o cadastro
    if (isDoctor(currentUser)) {
      if (
        !currentUser.address ||
        !currentUser.address.id ||
        !currentUser.bank_account
      ) {
        return "/guide";
      }
      return "/dashboard";
    }

    // Conta de equipe de clínica vai para o módulo Clínica (US-C12); a
    // recepção antiga, sem clínica, segue no painel antigo.
    if (isReceptionist(loginUser) || isReceptionist(currentUser)) {
      const units = loginUser.unit_ids || currentUser.unit_ids || [];
      return units.length ? "/clinica" : "/dashboard";
    }

    // Fallback para contas verificadas
    if (state.verifiedAccount) return "/dashboard";

    return "/";
  },

  getterAlertError(state) {
    return state.alertError;
  },

  getterUserGuide(state) {
    return state.userGuide;
  },

  getterVerifiedAccount(state) {
    return state.verifiedAccount;
  },

  hasActiveRelationshipWithInstitutions(state) {
    return state.hasActiveRelationshipWithInstitutions;
  },

  isSubmitting(state) {
    return state.isSubmitting;
  }
};

export const mutations = {
  SET_LOGGED_IN(state, loggedIn) {
    state.loggedIn = loggedIn;
  },

  SET_ALERT_ERROR(state, error) {
    state.alertError = error;
  },

  SET_REDIRECT_FROM(state, redirectFrom) {
    state.redirectFrom = redirectFrom;
  },

  SET_USER_GUIDE(state, userGuide) {
    state.userGuide = userGuide;
  },

  SET_VERIFIED_ACCOUNT(state, verifiedAccount) {
    state.verifiedAccount = verifiedAccount;
  },

  SET_LOGGED_USER(state, loggedUser) {
    state.loggedUser = loggedUser;
  },

  SET_IS_SUBMITTING(state, isSubmitting) {
    state.isSubmitting = isSubmitting;
  },

  SET_HAS_ACTIVE_RELATIONSHIP_WITH_INSTITUTIONS(state, value) {
    state.hasActiveRelationshipWithInstitutions = value;
  }
};

export const actions = {
  async resolvePostLoginRedirect({
    state,
    commit,
    dispatch,
    getters,
    rootState,
    rootGetters
  }) {
    const currentUser = rootState.user.currentUser;

    if (!isDoctor(currentUser)) return getters.redirectPathName;

    if (!doctorHasBasicInfo(currentUser)) {
      commit("SET_USER_GUIDE", true);
      return "/guide";
    }

    if (!(await fetchHasSlots(dispatch, currentUser))) {
      commit("SET_USER_GUIDE", true);
      return "/guide";
    }

    const { done } = await fetchDocsStatus(dispatch, rootGetters);
    if (!done) {
      commit("SET_USER_GUIDE", true);
      return "/guide";
    }

    return state.redirectFrom || "/dashboard";
  },

  login({ commit, dispatch, getters }, payload) {
    commit("SET_REDIRECT_FROM", payload.redirectFrom);
    commit("SET_IS_SUBMITTING", true);

    return Auth.login(payload)
      .then(response => {
        commit("SET_VERIFIED_ACCOUNT", response.data.verified_account);
        commit(
          "SET_HAS_ACTIVE_RELATIONSHIP_WITH_INSTITUTIONS",
          response.data["has_active_relationship_with_institutions?"]
        );
        commit("SET_LOGGED_USER", response.data);
        commit("SET_LOGGED_IN", true);
        commit("SET_IS_SUBMITTING", false);
        // Quem é tester é decidido no backend; a resposta do login já traz.
        dispatch("preLaunch/setStatus", response.data.pre_launch, {
          root: true
        });
        return dispatch("user/showUser", response.data.id, {
          root: true
        })
          .then(() => dispatch("resolvePostLoginRedirect"))
          .catch(error => error);
      })
      .catch(error => {
        commit("SET_IS_SUBMITTING", false);

        // Com o Modo Pré-Lançamento ligado o backend RECUSA a sessão de quem
        // está fora do snapshot: as credenciais estavam certas, o acesso é que
        // ainda não abriu. Mostrar "e-mail e/ou senha incorretos" aqui mandaria
        // a pessoa tentar recuperar uma senha que funciona.
        if (isPreLaunchBlocked(error)) {
          dispatch("preLaunch/markBlocked", preLaunchBlockedMessage(error), {
            root: true
          });
          router.push({ name: "PreLaunch" }).catch(() => {});
          return error;
        }

        if (error.response.status === 401)
          commit("SET_ALERT_ERROR", !state.alertError);
        this._vm.$toast.error("E-mail e/ou senha estão incorretos");
        return error;
      });
  },

  logout({ dispatch }) {
    return Auth.logout()
      .then(() => dispatch("clearLocalSession"))
      .catch(() => dispatch("clearLocalSession"));
  },

  // Só limpa o estado local (Vuex + storage) e redireciona, sem chamar a API
  // de sign_out. Usado quando a sessão já não é mais válida no backend (ex:
  // depois de desativar a própria conta — o token não resolve mais pra um
  // usuário ativo, então chamar /auth/sign_out só geraria um 401 desnecessário).
  clearLocalSession({ commit, dispatch }) {
    dispatch("appointments/clearScheduleAppointment", {}, { root: true });
    // sessionStorage.clear() abaixo derruba o estado persistido; refaz o
    // bootstrap para o visitante deslogado ver a tela certa.
    dispatch("preLaunch/fetchStatus", null, { root: true });
    commit("SET_LOGGED_IN", false);
    commit("SET_LOGGED_USER", {});
    commit("SET_USER_GUIDE", false);
    localStorage.removeItem("specialty");
    localStorage.removeItem("category");
    sessionStorage.clear();
    router.push({ name: "Login" });
  },

  register({ commit, dispatch, getters }, payload) {
    commit("SET_REDIRECT_FROM", payload.redirectFrom);
    commit("SET_IS_SUBMITTING", true);

    return Auth.register(payload)
      .then(async response => {
        commit("SET_LOGGED_IN", true);
        commit("SET_IS_SUBMITTING", false);
        // Cadastro é real, mas usuário novo nunca entra no snapshot de testers.
        dispatch("preLaunch/setStatus", response.data.pre_launch, {
          root: true
        });
        await dispatch("user/showUser", response.data.id, { root: true });
        return dispatch("resolvePostLoginRedirect");
      })
      .catch(error => {
        commit("SET_IS_SUBMITTING", false);
        const passwordErrors = error.response?.data?.errors?.password;
        if (passwordErrors?.length) {
          const messages = Array.isArray(passwordErrors)
            ? passwordErrors.join(", ")
            : passwordErrors;
          this._vm.$toast.error(`Senha inválida: ${messages}`);
        } else {
          this._vm.$toast.error(
            "Erro ao realizar cadastro, tente novamente mais tarde!"
          );
        }
        return error;
      });
  },

  forgotPassword({ commit, state }, payload) {
    return Auth.forgotPassword(payload).catch(error => {
      if (error.response.status == 404) {
        commit("SET_ALERT_ERROR", !state.alertError);
      }
      return error;
    });
  },

  updatePassword({ commit, dispatch, getters }, payload) {
    return Auth.updatePassword(payload)
      .then(response => {
        commit("SET_LOGGED_IN", true);
        dispatch("user/showUser", response.data.data.id, {
          root: true
        }).then(() => {
          router.push({ path: getters.redirectPathName });
        });
        return response;
      })
      .catch(error => {
        const passwordErrors = error.response?.data?.errors?.password;
        if (passwordErrors?.length) {
          const messages = Array.isArray(passwordErrors)
            ? passwordErrors.join(", ")
            : passwordErrors;
          this._vm.$toast.error(`Senha inválida: ${messages}`);
        } else {
          this._vm.$toast.error(
            "Erro ao atualizar senha, tente novamente mais tarde!"
          );
        }
        return error;
      });
  }
};
